import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/services/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  ResponsiveContainer, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import {
  Building2, FileText, Wallet, Clock, CheckCircle2,
  AlertTriangle, Users, TrendingUp, Package, Calendar,
  ArrowRight, BadgePercent,
} from "lucide-react";

// ── helpers ────────────────────────────────────────────────────────────────────
const fmtInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtShort = (n) => {
  const v = Number(n || 0);
  if (v >= 10_00_000)  return `₹${(v / 10_00_000).toFixed(1)}L`;
  if (v >= 1_000)      return `₹${(v / 1_000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const STATUS_COLORS = {
  Paid:      "#10b981", // emerald-500
  Pending:   "#f59e0b", // amber-500
  Overdue:   "#ef4444", // red-500
  Cancelled: "#94a3b8", // slate-400
};

const CHART_TOOLTIP_STYLE = {
  contentStyle: { background: "#0A0A0B", border: "none", color: "#fff", borderRadius: 4, fontSize: 12 },
  cursor: { fill: "rgba(255,255,255,0.04)" },
};

// ── sub-components ─────────────────────────────────────────────────────────────
function MetricCard({ icon: Icon, label, value, sub, accent, warn, testid }) {
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="border border-slate-200 dark:border-slate-800 p-5 rounded-sm bg-white dark:bg-[#121214] hover:border-slate-900 dark:hover:border-white transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${warn ? "text-red-500" : "text-slate-400"}`} />
      </div>
      <div className={`text-2xl font-extrabold tracking-tighter leading-none ${accent || ""}`}>{value}</div>
      {sub && <div className="mt-1.5 text-xs text-slate-500">{sub}</div>}
    </motion.div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-4">{children}</div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid:      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    pending:   "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    overdue:   "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    cancelled: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  };
  return (
    <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.15em] ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

// Skeleton helpers
function SkeletonBox({ className }) {
  return <div className={`bg-slate-200 dark:bg-slate-800 rounded animate-pulse ${className}`} />;
}

function DashboardSkeleton() {
  return (
    <div>
      <div className="mb-10">
        <SkeletonBox className="h-3 w-24 mb-2" />
        <SkeletonBox className="h-10 w-56" />
      </div>
      {/* metric row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="border border-slate-200 dark:border-slate-800 p-5 rounded-sm">
            <SkeletonBox className="h-3 w-20 mb-4" />
            <SkeletonBox className="h-7 w-24" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="border border-slate-200 dark:border-slate-800 p-5 rounded-sm">
            <SkeletonBox className="h-3 w-20 mb-4" />
            <SkeletonBox className="h-7 w-24" />
          </div>
        ))}
      </div>
      {/* chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <div className="lg:col-span-2 border border-slate-200 dark:border-slate-800 rounded-sm p-6">
          <SkeletonBox className="h-72" />
        </div>
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm p-6">
          <SkeletonBox className="h-72" />
        </div>
      </div>
    </div>
  );
}

// Custom recharts label for Pie
function PieLabel({ cx, cy, midAngle, outerRadius, percent, name }) {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const r  = outerRadius + 22;
  const x  = cx + r * Math.cos(-midAngle * RADIAN);
  const y  = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#94a3b8" fontSize={11} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central">
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

// ── main page ──────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [shouldShowDashboard, setShouldShowDashboard] = React.useState(false);

  React.useEffect(() => {
    queryClient.invalidateQueries(["dashboard"]);
  }, [queryClient]);

  const { data, isFetching } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
  });

  React.useEffect(() => {
    if (!isFetching && data) {
      if (data.total_organizations === 0) {
        nav("/app/onboarding");
      } else {
        setShouldShowDashboard(true);
      }
    }
  }, [isFetching, data, nav]);

  if (isFetching || !shouldShowDashboard) return <DashboardSkeleton />;

  const d = data;

  // Pie data — filter out zero-count slices
  const pieData = (d.status_breakdown || []).filter((s) => s.count > 0);

  // Top customers — truncate long names
  const topCustomers = (d.top_customers || []).map((c) => ({
    ...c,
    short_name: c.customer_name.length > 14 ? c.customer_name.slice(0, 13) + "…" : c.customer_name,
  }));

  const hasNegativeStock = (d.negative_stock || []).length > 0;
  const hasLowStock    = (d.low_stock || []).length > 0;
  const hasUpcoming    = (d.upcoming_dues || []).length > 0;

  return (
    <div>
      {/* ── Header ── */}
      <div className="mb-10">
        <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Overview</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter">Dashboard.</h1>
      </div>

      {/* ── Row 1: Financial summary ── */}
      <div className="mb-2">
        <SectionHeading>Financial summary</SectionHeading>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <MetricCard
          testid="metric-billed"
          icon={Wallet}
          label="Total Billed"
          value={fmtInr(d.total_billed)}
          sub={`${d.total_invoices} invoices`}
        />
        <MetricCard
          testid="metric-collected"
          icon={CheckCircle2}
          label="Total Collected"
          value={fmtInr(d.total_collected)}
          sub={`${d.paid_count} paid`}
          accent="text-emerald-600"
        />
        <MetricCard
          testid="metric-outstanding"
          icon={Clock}
          label="Outstanding"
          value={fmtInr(d.total_outstanding)}
          sub={`${d.open_count} open invoices`}
          accent={d.total_outstanding > 0 ? "text-amber-600" : ""}
        />
        <MetricCard
          testid="metric-collection-rate"
          icon={BadgePercent}
          label="Collection Rate"
          value={`${d.collection_rate}%`}
          sub="of total billed recovered"
          accent={d.collection_rate >= 80 ? "text-emerald-600" : d.collection_rate >= 50 ? "text-amber-600" : "text-red-600"}
        />
      </div>

      {/* ── Row 2: Activity this month + overdue + customers + orgs ── */}
      <div className="mb-2">
        <SectionHeading>This month &amp; status</SectionHeading>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <MetricCard
          testid="metric-month-billed"
          icon={TrendingUp}
          label="Billed This Month"
          value={fmtInr(d.month_billed)}
          sub={`${d.month_invoices} invoices raised`}
          accent="text-[#002FA7]"
        />
        <MetricCard
          testid="metric-month-collected"
          icon={CheckCircle2}
          label="Collected This Month"
          value={fmtInr(d.month_collected)}
          accent="text-emerald-600"
        />
        <MetricCard
          testid="metric-overdue"
          icon={AlertTriangle}
          label="Overdue"
          value={fmtInr(d.overdue_amount)}
          sub={`${d.overdue_count} overdue invoice${d.overdue_count !== 1 ? "s" : ""}`}
          accent={d.overdue_count > 0 ? "text-red-600" : ""}
          warn={d.overdue_count > 0}
        />
        <MetricCard
          testid="metric-customers"
          icon={Users}
          label="Customers"
          value={d.total_customers}
          sub={`across ${d.total_organizations} org${d.total_organizations !== 1 ? "s" : ""}`}
        />
      </div>

      {/* ── Row 3: Revenue trend + Invoice status pie ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        {/* Revenue vs Collected trend */}
        <div className="lg:col-span-2 border border-slate-200 dark:border-slate-800 rounded-sm p-6 bg-white dark:bg-[#121214]">
          <div className="flex items-center justify-between mb-4">
            <SectionHeading>Revenue trend — last 6 months</SectionHeading>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.trend || []} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.12} />
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtShort} width={52} />
                <Tooltip
                  {...CHART_TOOLTIP_STYLE}
                  formatter={(val, name) => [fmtInr(val), name === "billed" ? "Billed" : "Collected"]}
                />
                <Legend
                  formatter={(val) => val === "billed" ? "Billed" : "Collected"}
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                />
                <Line type="monotone" dataKey="billed"    stroke="#002FA7" strokeWidth={2.5} dot={{ r: 3 }} name="billed" />
                <Line type="monotone" dataKey="collected" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} strokeDasharray="4 2" name="collected" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Invoice status breakdown — pie */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm p-6 bg-white dark:bg-[#121214]">
          <SectionHeading>Invoice status</SectionHeading>
          {pieData.length > 0 ? (
            <>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={72}
                      labelLine={false}
                      label={PieLabel}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || "#94a3b8"} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#0A0A0B", border: "none", color: "#fff", borderRadius: 4, fontSize: 12 }}
                      formatter={(val, name) => [`${val} invoice${val !== 1 ? "s" : ""}`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {pieData.map((s) => (
                  <div key={s.status} className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS_COLORS[s.status] }} />
                    <span className="text-xs text-slate-600 dark:text-slate-400">{s.status}</span>
                    <span className="text-xs font-semibold ml-auto">{s.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-52 flex items-center justify-center text-slate-400 text-sm">No invoices yet</div>
          )}
        </div>
      </div>

      {/* ── Row 4: Top customers bar + Invoice volume bar ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
        {/* Top customers by billed amount */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm p-6 bg-white dark:bg-[#121214]">
          <div className="flex items-center justify-between mb-4">
            <SectionHeading>Top customers by revenue</SectionHeading>
            <Link to="/app/customers" className="text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:text-[#002FA7] flex items-center gap-1">
              All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {topCustomers.length > 0 ? (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topCustomers}
                  layout="vertical"
                  margin={{ top: 0, right: 48, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} horizontal={false} />
                  <XAxis type="number" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtShort} />
                  <YAxis type="category" dataKey="short_name" width={88} fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(val, name) => [
                      fmtInr(val),
                      name === "total_billed" ? "Billed" : "Outstanding",
                    ]}
                  />
                  <Bar dataKey="total_billed"  fill="#002FA7" radius={[0, 3, 3, 0]} name="total_billed" />
                  <Bar dataKey="outstanding"   fill="#f59e0b" radius={[0, 3, 3, 0]} name="outstanding" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-52 flex items-center justify-center text-slate-400 text-sm">No customer data yet</div>
          )}
          {topCustomers.length > 0 && (
            <div className="flex gap-4 mt-3">
              <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#002FA7]" /> Billed
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400" /> Outstanding
              </span>
            </div>
          )}
        </div>

        {/* Monthly invoice volume */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-sm p-6 bg-white dark:bg-[#121214]">
          <SectionHeading>Invoice volume — last 6 months</SectionHeading>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.trend || []} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.12} />
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  {...CHART_TOOLTIP_STYLE}
                  formatter={(val) => [`${val} invoice${val !== 1 ? "s" : ""}`, "Count"]}
                />
                <Bar dataKey="count" fill="#0A0A0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Row 5: Alerts (negative stock + low stock + upcoming dues) ── */}
      {(hasNegativeStock || hasLowStock || hasUpcoming) && (
        <div className={`grid gap-4 mb-10 ${hasNegativeStock && hasLowStock && hasUpcoming ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {/* Negative stock */}
          {hasNegativeStock && (
            <div className="border border-red-200 dark:border-red-900/40 rounded-sm bg-white dark:bg-[#121214]">
              <div className="p-5 flex items-center gap-2 border-b border-red-100 dark:border-red-900/30">
                <Package className="w-4 h-4 text-red-500" />
                <span className="uppercase text-[10px] tracking-[0.2em] text-red-500 font-semibold">Negative stock alerts</span>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {d.negative_stock.map((item) => (
                  <div key={item.id} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <div className="text-sm font-semibold">{item.item_name}</div>
                      <div className="text-xs text-slate-500">{item.organization_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-red-600">
                        {Number(item.quantity).toLocaleString("en-IN")} {item.unit}
                      </div>
                      <div className="text-xs text-slate-500">
                        threshold: {Number(item.low_stock_threshold).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-red-100 dark:border-red-900/30">
                <Link
                  to="/app/inventory"
                  className="text-xs text-red-600 hover:underline flex items-center gap-1"
                >
                  Manage inventory <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}

          {/* Low stock */}
          {hasLowStock && (
            <div className="border border-amber-200 dark:border-amber-900/40 rounded-sm bg-white dark:bg-[#121214]">
              <div className="p-5 flex items-center gap-2 border-b border-amber-100 dark:border-amber-900/30">
                <Package className="w-4 h-4 text-amber-500" />
                <span className="uppercase text-[10px] tracking-[0.2em] text-amber-500 font-semibold">Low stock alerts</span>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {d.low_stock.map((item) => (
                  <div key={item.id} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <div className="text-sm font-semibold">{item.item_name}</div>
                      <div className="text-xs text-slate-500">{item.organization_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-amber-600">
                        {Number(item.quantity).toLocaleString("en-IN")} {item.unit}
                      </div>
                      <div className="text-xs text-slate-500">
                        threshold: {Number(item.low_stock_threshold).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-amber-100 dark:border-amber-900/30">
                <Link
                  to="/app/inventory"
                  className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                >
                  Manage inventory <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}

          {/* Upcoming dues */}
          {hasUpcoming && (
            <div className="border border-amber-200 dark:border-amber-900/40 rounded-sm bg-white dark:bg-[#121214]">
              <div className="p-5 flex items-center gap-2 border-b border-amber-100 dark:border-amber-900/30">
                <Calendar className="w-4 h-4 text-amber-500" />
                <span className="uppercase text-[10px] tracking-[0.2em] text-amber-600 font-semibold">Due in next 7 days</span>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {d.upcoming_dues.map((inv) => (
                  <Link
                    key={inv.id}
                    to={`/app/invoices/${inv.id}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                  >
                    <div>
                      <div className="text-sm font-semibold">{inv.invoice_number}</div>
                      <div className="text-xs text-slate-500">{inv.customer_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-amber-600">{fmtInr(inv.outstanding)}</div>
                      <div className="text-xs text-slate-500">due {inv.due_date}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Row 6: Recent invoices ── */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214]">
        <div className="p-5 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
          <SectionHeading>Recent invoices</SectionHeading>
          <Link
            to="/app/invoices"
            className="text-xs underline underline-offset-4 hover:text-[#002FA7]"
            data-testid="view-all-invoices-link"
          >
            View all →
          </Link>
        </div>

        {/* Table header */}
        <div className="hidden sm:grid grid-cols-[27.5%_27.5%_22%_8%_15%] gap-4 px-5 py-2.5 bg-slate-50 dark:bg-slate-900 text-[10px] uppercase tracking-[0.2em] text-slate-400">
          <span>Invoice</span>
          <span>Customer</span>
          <span className="text-right">Total</span>
          <span className="text-right">Outstanding</span>
          <span>Status</span>
        </div>

        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {(d.recent || []).length === 0 && (
            <div className="p-10 text-center text-slate-500" data-testid="empty-recent-invoices">
              No invoices yet. Create your first one.
            </div>
          )}
          {(d.recent || []).map((inv) => (
            <Link
              key={inv.id}
              to={`/app/invoices/${inv.id}`}
              className="grid grid-cols-[1fr_auto] sm:grid-cols-[27.5%_27.5%_22%_8%_15%] gap-4 items-center px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
              data-testid={`recent-invoice-${inv.id}`}
            >
              <div>
                <div className="font-semibold text-sm">{inv.invoice_number}</div>
                <div className="text-xs text-slate-500">{inv.invoice_date}{inv.due_date ? ` · due ${inv.due_date}` : ""}</div>
              </div>
              <div className="hidden sm:block text-sm text-slate-600 dark:text-slate-400 truncate">
                {inv.customer_name}
              </div>
              <div className="text-right">
                <div className="font-semibold text-sm">{fmtInr(inv.total)}</div>
              </div>
              <div className="hidden sm:block text-right">
                <div className={`text-sm font-semibold ${Number(inv.outstanding) > 0 ? "text-amber-600" : "text-slate-400"}`}>
                  {Number(inv.outstanding) > 0 ? fmtInr(inv.outstanding) : "—"}
                </div>
              </div>
              <div>
                <StatusBadge status={inv.status} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
